import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BelagomProfilePhoto from '../components/BelagomProfilePhoto';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, Button } from '../components';
import { 
  Search, Filter, Star, MapPin, Clock,
  Languages, Phone, Mail,
  Heart, ChevronDown, ChevronUp
} from 'lucide-react';
import { useProfessionalSearch } from '../hooks/useProfessionalSearch';
import { useTranslation } from 'react-i18next';

// Search filters schema
const searchFiltersSchema = z.object({
  professional_type: z.enum([
    'psycholoog_coach',
    'financieel_coach',
    'loopbaancoach',
    'teamcoach',
    'vitaliteitscoach',
    'burnout_coach',
    'vertrouwenspersoon',
    'bedrijfsmaatschappelijk_werker',
    'mediator'
  ]),
  languages: z.array(z.string()).optional(),
  availability_status: z.enum(['immediately', 'specific_date', 'any']).optional(),
  available_from_date: z.string().optional(),
  active_regions: z.array(z.string()).optional(),
  // Werkwijze en uren-per-week zijn als zoekfilter vervallen (FO); specialisme-
  // en doelgroeptags sturen het zoeken nu.
  specialisatie_tags: z.array(z.string()).optional(),
  doelgroep_niveaus: z.array(z.string()).optional(),
  hourly_rate_min: z.number().min(50).optional(),
  hourly_rate_max: z.number().max(200).optional(),
  years_experience_min: z.number().min(0).optional(),
  years_experience_max: z.number().max(50).optional(),
  industry_experience: z.array(z.string()).optional(),
  min_rating: z.number().min(1).max(5).optional()
});

type SearchFilters = z.infer<typeof searchFiltersSchema>;

interface Professional {
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  profile_photo_path?: string;
  professional_type: 'psycholoog_coach' | 'financieel_coach' | 'loopbaancoach' | 'teamcoach' | 'vitaliteitscoach' | 'burnout_coach' | 'vertrouwenspersoon' | 'bedrijfsmaatschappelijk_werker' | 'mediator';
  availability_status: 'not_available' | 'immediately' | 'specific_date';
  available_from_date?: string;
  languages: string[];
  specialisatie_tags?: string[];
  company_name?: string | null;
  active_regions: string[];
  industry_experience: string[];
  city: string;
  average_rating?: number;
  total_reviews: number;
  email: string;
  phone: string;
}

export const ProfessionalSearchPage: React.FC = () => {
  const { t } = useTranslation('professional');
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [savedProfessionals, setSavedProfessionals] = useState<string[]>([]);

  const { register, handleSubmit, watch, setValue } = useForm<SearchFilters>({
    resolver: zodResolver(searchFiltersSchema),
    defaultValues: {
      professional_type: 'bedrijfsmaatschappelijk_werker',
      availability_status: 'any'
    }
  });

  const watchedValues = watch();
  const { data: searchResults, isLoading, refetch } = useProfessionalSearch(watchedValues);

  // Professional type options (ordered according to PROFESSIONAL_TYPE_ORDER)
  const professionalTypes = [
    { value: 'psycholoog_coach', label: 'Psycholoog-Coach (GZ/Organisatie)' },
    { value: 'financieel_coach', label: 'Financieel Coach / Budgetcoach' },
    { value: 'loopbaancoach', label: 'Loopbaancoach' },
    { value: 'teamcoach', label: 'Teamcoach' },
    { value: 'vitaliteitscoach', label: 'Vitaliteitscoach' },
    { value: 'burnout_coach', label: 'Burn-out Coach' },
    { value: 'vertrouwenspersoon', label: 'Vertrouwenspersoon' },
    { value: 'bedrijfsmaatschappelijk_werker', label: 'Bedrijfsmaatschappelijk Werker' },
    { value: 'mediator', label: 'Mediator' }
  ];

  // Dutch provinces
  const dutchProvinces = [
    'Noord-Holland', 'Zuid-Holland', 'Utrecht', 'Noord-Brabant', 'Gelderland',
    'Limburg', 'Overijssel', 'Friesland', 'Groningen', 'Drenthe', 'Flevoland', 'Zeeland'
  ];

  const commonLanguages = ['Nederlands', 'Engels', 'Duits', 'Frans', 'Spaans'];
  const commonIndustries = [
    'Sport', 'Financiën', 'ICT', 'Gezondheidszorg', 'Onderwijs', 'Retail',
    'Manufacturing', 'Logistiek', 'Juridisch', 'HR', 'Marketing', 'Overheid'
  ];

  const availabilityOptions = [
    { value: 'any', label: 'Alle beschikbaarheden' },
    { value: 'immediately', label: 'Per direct beschikbaar' },
    { value: 'specific_date', label: 'Vanaf specifieke datum' }
  ];

  // Get rate ranges based on experience
  const handleSaveProfessional = (professionalId: string) => {
    setSavedProfessionals(prev => {
      if (prev.includes(professionalId)) {
        return prev.filter(id => id !== professionalId);
      }
      return [...prev, professionalId];
    });
  };

  const handleContactProfessional = (professional: Professional) => {
    // Open email client
    const subject = encodeURIComponent(`BeLagom - Interesse in jouw diensten als ${professional.professional_type.replace('_', ' ')}`);
    const body = encodeURIComponent(`Beste ${professional.first_name},\n\nIk heb jouw profiel gezien op BeLagom en ben geïnteresseerd in jouw diensten.\n\nGraag zou ik een kennismakingsgesprek plannen.\n\nMet vriendelijke groet,`);
    window.open(`mailto:${professional.email}?subject=${subject}&body=${body}`);
  };

  const onSubmit = (_data: SearchFilters) => {
    refetch();
  };

  const clearFilters = () => {
    setValue('languages', []);
    setValue('active_regions', []);
    setValue('industry_experience', []);
    setValue('availability_status', 'any');
    setValue('specialisatie_tags', []);
    setValue('doelgroep_niveaus', []);
    setValue('hourly_rate_min', undefined);
    setValue('hourly_rate_max', undefined);
    setValue('years_experience_min', undefined);
    setValue('years_experience_max', undefined);
    setValue('min_rating', undefined);
  };

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('search.title')}</h1>
          <p className="mt-2 text-gray-600">
            {t('search.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-8">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Filter className="w-5 h-5 mr-2" />
                    {t('search.filters')}
                  </h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                    className="lg:hidden"
                  >
                    {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </Button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className={`space-y-6 ${showFilters ? 'block' : 'hidden lg:block'}`}>
                  {/* Professional Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('search.professionalType')}
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      {...register('professional_type')}
                    >
                      {professionalTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Languages */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('search.languages')}
                    </label>
                    <div className="space-y-2">
                      {commonLanguages.map((language) => (
                        <label key={language} className="flex items-center">
                          <input
                            type="checkbox"
                            value={language}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                            {...register('languages')}
                          />
                          <span className="text-sm">{language}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Availability */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('search.availability')}
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      {...register('availability_status')}
                    >
                      {availabilityOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {watchedValues.availability_status === 'specific_date' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('search.availableFrom')}
                      </label>
                      <input
                        type="date"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        {...register('available_from_date')}
                      />
                    </div>
                  )}

                  {/* Regions */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Regio's
                    </label>
                    <div className="max-h-40 overflow-y-auto space-y-2">
                      {dutchProvinces.map((province) => (
                        <label key={province} className="flex items-center">
                          <input
                            type="checkbox"
                            value={province}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                            {...register('active_regions')}
                          />
                          <span className="text-sm">{province}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Hourly Rate Range */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Uurtarief (€)
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="number"
                        placeholder="Min"
                        min="50"
                        max="200"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        {...register('hourly_rate_min', { valueAsNumber: true })}
                      />
                      <input
                        type="number"
                        placeholder="Max"
                        min="50"
                        max="200"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        {...register('hourly_rate_max', { valueAsNumber: true })}
                      />
                    </div>
                  </div>

                  {/* Experience Range */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ervaring (jaren zelfstandig)
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="number"
                        placeholder="Min"
                        min="0"
                        max="50"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        {...register('years_experience_min', { valueAsNumber: true })}
                      />
                      <input
                        type="number"
                        placeholder="Max"
                        min="0"
                        max="50"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        {...register('years_experience_max', { valueAsNumber: true })}
                      />
                    </div>
                  </div>

                  {/* Industry Experience */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Affiniteit
                    </label>
                    <div className="max-h-40 overflow-y-auto space-y-2">
                      {commonIndustries.map((industry) => (
                        <label key={industry} className="flex items-center">
                          <input
                            type="checkbox"
                            value={industry}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                            {...register('industry_experience')}
                          />
                          <span className="text-sm">{industry}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Minimum Rating */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('search.minRating')}
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      {...register('min_rating', { valueAsNumber: true })}
                    >
                      <option value="">{t('search.allRatings')}</option>
                      <option value={5}>{t('search.stars5')}</option>
                      <option value={4}>{t('search.stars4')}</option>
                      <option value={3}>{t('search.stars3')}</option>
                      <option value={2}>{t('search.stars2')}</option>
                      <option value={1}>{t('search.stars1')}</option>
                    </select>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3 pt-4 border-t">
                    <Button type="submit" className="w-full" disabled={isLoading}>
                      <Search className="w-4 h-4 mr-2" />
                      {isLoading ? 'Zoeken...' : 'Zoeken'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={clearFilters}
                    >
                      Filters Wissen
                    </Button>
                  </div>
                </form>
              </div>
            </Card>
          </div>

          {/* Search Results */}
          <div className="lg:col-span-3">
            {/* Search Bar */}
            <Card className="mb-6">
              <div className="p-4">
                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder={t('search.searchPlaceholder')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <Button onClick={handleSubmit(onSubmit)}>
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>

            {/* Results Count */}
            {searchResults && (
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  {searchResults.length} professional{searchResults.length !== 1 ? 's' : ''} gevonden
                </p>
              </div>
            )}

            {/* Professional Cards */}
            <div className="space-y-6">
              {searchResults?.map((professional: Professional) => (
                <Card key={professional.id} className="hover:shadow-lg transition-shadow">
                  <div className="p-6">
                    <div className="flex items-start space-x-4">
                      {/* Profile Photo */}
                      <BelagomProfilePhoto
                        src={professional.profile_photo_path}
                        alt={`${professional.first_name} ${professional.last_name}`}
                        size={64}
                      />

                      {/* Professional Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="text-xl font-semibold text-gray-900">
                              {professional.first_name} {professional.last_name}
                            </h3>
                            <p className="text-blue-600 font-medium capitalize">
                              {professional.professional_type.replace('_', ' ')}
                            </p>
                          </div>
                          
                          <div className="flex items-center space-x-2 flex-shrink-0">
                            <button
                              onClick={() => handleSaveProfessional(professional.id)}
                              className={`p-2 rounded-full ${
                                savedProfessionals.includes(professional.id)
                                  ? 'text-red-500 bg-red-50'
                                  : 'text-gray-400 hover:text-red-500'
                              }`}
                            >
                              <Heart className={`w-5 h-5 ${
                                savedProfessionals.includes(professional.id) ? 'fill-current' : ''
                              }`} />
                            </button>
                          </div>
                        </div>

                        {/* Rating */}
                        {professional.average_rating && (
                          <div className="flex items-center mt-2">
                            <div className="flex items-center">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`w-4 h-4 ${
                                    i < Math.floor(professional.average_rating!)
                                      ? 'text-yellow-400 fill-current'
                                      : 'text-gray-300'
                                  }`}
                                />
                              ))}
                            </div>
                            <span className="ml-2 text-sm text-gray-600">
                              {typeof professional.average_rating === 'number' 
                                ? professional.average_rating.toFixed(1)
                                : parseFloat(String(professional.average_rating || 0)).toFixed(1)} ({professional.total_reviews || 0} reviews)
                            </span>
                          </div>
                        )}

                        {/* Key Information */}
                        <div className="grid md:grid-cols-2 gap-4 mt-4">
                          <div className="space-y-2">
                            <div className="flex items-center text-sm text-gray-600">
                              <MapPin className="w-4 h-4 mr-2" />
                              {professional.city}
                            </div>
                            
                            <div className="flex items-center text-sm text-gray-600">
                              <Clock className="w-4 h-4 mr-2" />
                              {professional.availability_status === 'immediately' ? 'Per direct beschikbaar' :
                               professional.availability_status === 'specific_date' ? `Vanaf ${professional.available_from_date}` :
                               'Niet beschikbaar'}
                            </div>

                          </div>

                          <div className="space-y-2">
                            <div className="flex items-center text-sm text-gray-600">
                              <Languages className="w-4 h-4 mr-2" />
                              {professional.languages.join(', ')}
                            </div>

                          </div>
                        </div>

                        {/* Specialisaties & branches (jaren zelfstandig is uit het
                            openbare profiel verwijderd — FO) */}
                        <div className="mt-4">
                          {(professional.specialisatie_tags || []).length > 0 && (
                            <div className="mb-2 flex flex-wrap gap-2">
                              {(professional.specialisatie_tags || []).map((tag) => (
                                <span key={tag} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}

                          {professional.industry_experience.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {professional.industry_experience.map((industry) => (
                                <span
                                  key={industry}
                                  className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full"
                                >
                                  {industry}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center space-x-3 mt-6">
                          <Button
                            onClick={() => handleContactProfessional(professional)}
                            className="flex items-center"
                          >
                            <Mail className="w-4 h-4 mr-2" />
                            Contact via E-mail
                          </Button>
                          
                          <Button
                            variant="outline"
                            onClick={() => window.open(`tel:${professional.phone}`, '_self')}
                            className="flex items-center"
                          >
                            <Phone className="w-4 h-4 mr-2" />
                            Bellen
                          </Button>

                          <Button
                            variant="outline"
                            onClick={() => {
                              // Navigate to detailed profile view
                              navigate(`/professionals/${professional.id}`);
                            }}
                          >
                            Volledig Profiel
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* No Results */}
            {searchResults && searchResults.length === 0 && (
              <Card>
                <div className="p-8 text-center">
                  <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Geen professionals gevonden
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Probeer je zoekfilters aan te passen of maak een opdracht aan zodat professionals op jou kunnen reageren.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/opdrachten/nieuw')}
                  >
                    Opdracht Aanmaken
                  </Button>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
